import { Dialog } from "@mui/material";
import { useScale } from "common/utils";
import { ConfirmMessageBox, ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/parent/i18n";
import { EntitlementDetail } from "features/subscription";

interface SubscriptionDialogsProps {
  showUpdate: boolean;
  showStoreCancelInfo: boolean;
  showPlatformMismatchError: boolean;
  showRestoreSuccess: boolean;
  entitlement: EntitlementDetail | null;
  onCloseUpgrade: () => void;
  onGotoUpgrade: () => void;
  onCloseStoreCancelInfo: () => void;
  onClosePlatformMismatchError: () => void;
  onCloseRestoreSuccess: () => void;
}

export function SubscriptionDialogs({
  showUpdate,
  showStoreCancelInfo,
  showPlatformMismatchError,
  showRestoreSuccess,
  entitlement,
  onCloseUpgrade,
  onGotoUpgrade,
  onCloseStoreCancelInfo,
  onClosePlatformMismatchError,
  onCloseRestoreSuccess,
}: SubscriptionDialogsProps) {
  const { scale } = useScale();

  return (
    <>
      {showUpdate && (
        <ConfirmMessageBox
          title={i18n.t("MSG_APP_VERSION_UPDATE_WARNING_TITLE")}
          message={i18n.t("MSG_APP_VERSION_UPDATE_WARNING_MESSAGE")}
          close={onCloseUpgrade}
          confirm={onGotoUpgrade}
          is_parents
        />
      )}

      {showStoreCancelInfo && (
        <Dialog
          fullScreen
          open
          maxWidth={false}
          sx={{ zIndex: 90000 }}
          TransitionComponent={FadeTransition}
          PaperProps={{ style: { backgroundColor: "transparent" } }}
        >
          <div className="flex-col-center w-full h-full bg-textcolor-black/50">
            <div style={{ transform: `scale(${scale})` }}>
              <div className="flex-col-el w-[600px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
                <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
                  <ParentCloseBtn close={onCloseStoreCancelInfo} />
                  <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline">
                    <span className="material-symbols-outlined text-[36px] text-blue-100">
                      info
                    </span>
                    <h2 className="text-[30px] text-textcolor-black">
                      {i18n.t("MSG_SUBSCRIPTION_STORE_MANAGE_TITLE")}
                    </h2>
                  </div>
                  <p className="text-[24px] text-textcolor-black py-4">
                    {entitlement?.payment_provider === "app_store"
                      ? i18n.t("MSG_SUBSCRIPTION_MANAGE_APP_STORE_MESSAGE")
                      : i18n.t("MSG_SUBSCRIPTION_MANAGE_PLAY_STORE_MESSAGE")}
                  </p>
                  <div className="flex-row-el flex-center mt-6">
                    <button
                      onClick={onCloseStoreCancelInfo}
                      className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                    >
                      {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {showPlatformMismatchError && (
        <Dialog
          fullScreen
          open
          maxWidth={false}
          sx={{ zIndex: 90000 }}
          TransitionComponent={FadeTransition}
          PaperProps={{ style: { backgroundColor: "transparent" } }}
        >
          <div className="flex-col-center w-full h-full bg-textcolor-black/50">
            <div style={{ transform: `scale(${scale})` }}>
              <div className="flex-col-el w-[600px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
                <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
                  <ParentCloseBtn close={onClosePlatformMismatchError} />
                  <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline">
                    <span className="material-symbols-outlined text-[36px] text-danger">error</span>
                    <h2 className="text-[30px] text-textcolor-black">
                      {i18n.t("MSG_SUBSCRIPTION_PLATFORM_MISMATCH_TITLE")}
                    </h2>
                  </div>
                  <p className="text-[24px] text-textcolor-black py-4">
                    {i18n.t("MSG_SUBSCRIPTION_PLATFORM_MISMATCH_MESSAGE")}
                  </p>
                  <div className="flex-row-el flex-center mt-6">
                    <button
                      onClick={onClosePlatformMismatchError}
                      className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                    >
                      {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {showRestoreSuccess && (
        <Dialog
          fullScreen
          open
          maxWidth={false}
          sx={{ zIndex: 90000 }}
          TransitionComponent={FadeTransition}
          PaperProps={{ style: { backgroundColor: "transparent" } }}
        >
          <div className="flex-col-center w-full h-full bg-textcolor-black/50">
            <div style={{ transform: `scale(${scale})` }}>
              <div className="flex-col-el w-[600px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
                <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
                  <ParentCloseBtn close={onCloseRestoreSuccess} />
                  <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline">
                    <span className="material-symbols-outlined text-[36px] text-safe">
                      check_circle
                    </span>
                    <h2 className="text-[30px] text-textcolor-black">
                      {i18n.t("MSG_SUBSCRIPTION_RESTORE_SUCCESS_TITLE")}
                    </h2>
                  </div>
                  <p className="text-[24px] text-textcolor-black py-4">
                    {i18n.t("MSG_SUBSCRIPTION_RESTORE_SUCCESS_MESSAGE")}
                  </p>
                  <div className="flex-row-el flex-center mt-6">
                    <button
                      onClick={onCloseRestoreSuccess}
                      className="flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
                    >
                      {i18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}
